import { Download } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { formatDate, formatRange } from "../../Revenue/format";
import type { RowKind } from "./ActualsTable";

const SEARCH_PLACEHOLDER: Record<RowKind, string> = {
  products: "Search products…",
  categories: "Search categories…",
  channels: "Search channels…",
};

interface Props {
  period: { actual_from: string; actual_to: string; plan_from: string; plan_to: string };
  planSource: "by_month" | "by_day";
  wholesaleMonthlyOnly: boolean;
  rowKind: RowKind;
  query: string;
  onQueryChange: (query: string) => void;
  exportHref: string;
}

const range = (from: string, to: string) => (from === to ? formatDate(from) : formatRange(from, to));

// The table's dates and where its plan revenue comes from, with search and
// the sales export on the right.
export default function ActualsDateLine({ period, planSource, wholesaleMonthlyOnly, rowKind, query, onQueryChange, exportHref }: Props) {
  // The picked period misses the plan's dates entirely: the server sends from later than to.
  const outsidePlan = period.actual_from > period.actual_to || period.plan_from > period.plan_to;

  return (
    <div className="flex flex-wrap items-start gap-3 border-b px-4 py-3 text-sm">
      <div className="min-w-0 flex-1">
        {outsidePlan ? (
          <div className="flex min-h-9 items-center">This period is outside the plan's dates.</div>
        ) : (
          <>
            {/* As tall as the search box, so the dates line up with it. */}
            <div className="flex min-h-9 items-center">
              <span>
                Actual: <span className="font-medium">{range(period.actual_from, period.actual_to)}</span>
                <span className="mx-2 text-muted-foreground">·</span>
                Plan: <span className="font-medium">{range(period.plan_from, period.plan_to)}</span>
              </span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {planSource === "by_month" ? (
                <>
                  Plan revenue adds up the Bottom up By Month planning numbers at each product's target price,
                  the same as the By Month page. A product with no By Month plan shows -
                </>
              ) : (
                <>
                  Plan revenue adds up the Bottom up By Day planning numbers, each day at its own price.
                  A product not split into days yet shows -
                </>
              )}
            </div>
            {wholesaleMonthlyOnly && (
              <div className="mt-1 text-xs text-muted-foreground">
                Wholesale is planned by month, so it has no plan for part of a month. Pick a whole month to compare it.
              </div>
            )}
          </>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={SEARCH_PLACEHOLDER[rowKind]}
          className="h-9 w-56"
        />
        <Button asChild variant="outline" size="sm" className="h-9">
          <a href={exportHref}>
            <Download className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
            Export Sales CSV
          </a>
        </Button>
      </div>
    </div>
  );
}
